/**
 * Pure copy + shortfall math for the Request Guarantor pre-screen confirm
 * step (Figma: single/multi × wallet ok/short).
 */

export type GuarantorUpgradeConfirmInput = {
	/** Display name when exactly one applicant will upgrade. */
	applicantName: string
	/** Non-guarantor pre-screen applicants who will upgrade on guarantor submit. */
	upgradeApplicantCount: number
	/** Viewer wallet balance; null → no shortfall warning (no wallet). */
	walletBalance: number | null
	/** Per-applicant pre→full credit estimate; null → no shortfall warning. */
	upgradeCreditsPerApplicant: number | null
}

export type GuarantorUpgradeConfirmView = {
	/** Bold subject: applicant name or "All N Applicants". */
	subject: string
	isMulti: boolean
	/** Applicants included in the subject / shortfall math. */
	upgradeApplicantCount: number
	/**
	 * Credits still needed if every upgrade applicant completes. null when the
	 * warning should be hidden (unknown pricing/balance, or balance covers it).
	 */
	creditsShort: number | null
}

/** Format the confirm subtitle: "2100 Camilla Road, Unit 20". */
export function formatGuarantorConfirmAddress(property: {
	address: string
	unitNumber: string | null
}): string {
	const address = property.address.trim()
	const unit = property.unitNumber?.trim()
	if (!unit) return address
	const unitNeedle = `unit ${unit}`.toLowerCase()
	if (address.toLowerCase().includes(unitNeedle)) return address
	return address ? `${address}, Unit ${unit}` : `Unit ${unit}`
}

/**
 * Count pre-screen non-guarantors who will upgrade when the guarantor submits.
 * Solo pages pass an empty members list + the page's own screening kind.
 * When `upgradeEnabled` is false (student_housing), always returns 0.
 */
export function countGuarantorUpgradeApplicants(input: {
	members: Array<{
		applicationType: string
		screeningKind: string
	}>
	/** Individual solo page: true when THIS application is pre-screen. */
	soloWillUpgrade: boolean
	/** false for student_housing — no household pre→full on guarantor submit. */
	upgradeEnabled?: boolean
}): number {
	if (input.upgradeEnabled === false) return 0
	if (input.members.length === 0) {
		return input.soloWillUpgrade ? 1 : 0
	}
	return input.members.filter(
		(m) =>
			m.applicationType !== 'guarantor' && m.screeningKind === 'pre_screening',
	).length
}

export function deriveGuarantorUpgradeConfirm(
	input: GuarantorUpgradeConfirmInput,
): GuarantorUpgradeConfirmView {
	const count = Math.max(0, input.upgradeApplicantCount)
	const isMulti = count > 1
	const subject = isMulti
		? `All ${count} Applicants`
		: input.applicantName.trim() || 'This applicant'

	let creditsShort: number | null = null
	if (
		count > 0 &&
		input.walletBalance != null &&
		input.upgradeCreditsPerApplicant != null &&
		input.upgradeCreditsPerApplicant > 0
	) {
		const needed = count * input.upgradeCreditsPerApplicant
		const short = needed - input.walletBalance
		creditsShort = short > 0 ? Math.ceil(short) : null
	}

	return { subject, isMulti, upgradeApplicantCount: count, creditsShort }
}
